import * as React from 'react';
import { cn } from '../../lib/cn';

type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * Empty-state placeholder. Use when a list/table has no rows yet. The design
 * HTML composes an icon + title + body + CTA in a centered glass card.
 */
export interface EmptyStateProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode | IconComponent;
  action?: React.ReactNode;
}

/**
 * Distinguish a renderable component *type* (a function, or a forwardRef/memo
 * object such as every `lucide-react` icon) from an already-rendered node (a
 * JSX element, string, or number). lucide icons are `React.forwardRef`
 * objects, so a bare `typeof === "function"` check misclassifies them as
 * nodes and React then throws "Objects are not valid as a React child".
 */
function isComponentType(
  icon: React.ReactNode | IconComponent,
): icon is IconComponent {
  if (typeof icon === 'function') return true;
  if (typeof icon === 'object' && icon !== null && '$$typeof' in icon) {
    const tag = (icon as { $$typeof?: symbol }).$$typeof;
    return (
      tag === Symbol.for('react.forward_ref') ||
      tag === Symbol.for('react.memo')
    );
  }
  return false;
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ title, description, icon, action, className, ...props }, ref) => {
    let iconEl: React.ReactNode = null;
    if (icon !== null && icon !== undefined) {
      if (isComponentType(icon)) {
        const Icon = icon;
        iconEl = <Icon className="h-12 w-12" />;
      } else {
        iconEl = icon;
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center text-center py-10 px-6',
          className,
        )}
        {...props}
      >
        {iconEl !== null && (
          <div className="mb-3 text-ink-muted" aria-hidden="true">
            {iconEl}
          </div>
        )}
        <div className="text-[15px] font-semibold text-ink">{title}</div>
        {description !== undefined && (
          <div className="mt-1 text-[13px] text-ink-muted max-w-md">
            {description}
          </div>
        )}
        {action !== undefined && <div className="mt-4">{action}</div>}
      </div>
    );
  },
);
EmptyState.displayName = 'EmptyState';
